import { AlertColor, SxProps } from "@mui/material";
import { isFunction } from "lodash";
import { ReactNode } from "react";
import { create } from "zustand/react";

interface GlobalStore {
  toaster: {
    variant: AlertColor;
    message: string | ReactNode;
    open: boolean;
    duration?: number;
    sx?: SxProps;
    action?: ReactNode;
  };
  actions: {
    setToaster: (toaster: Partial<GlobalStore["toaster"]>) => void;
    setToasterPromise: (
      promise: Promise<unknown>,
      options: {
        onPending?: () => Partial<GlobalStore["toaster"]>;
        onSuccess?: () => Partial<GlobalStore["toaster"]>;
        onError?: () => Partial<GlobalStore["toaster"]>;
      },
    ) => Promise<void>;
  };
}

const defaultGlobalStore = {
  toaster: {
    variant: "success" as AlertColor,
    message: "",
    open: false,
    duration: 5000,
  },
};

export const useGlobalStore = create<GlobalStore>((set) => ({
  toaster: defaultGlobalStore.toaster,
  actions: {
    setToaster: (toaster) =>
      set((state) => ({
        toaster: {
          ...state.toaster,
          ...toaster,
        },
      })),
    setToasterPromise: async (
      promise: Promise<unknown>,
      options: {
        onPending?: () => Partial<GlobalStore["toaster"]>;
        onSuccess?: () => Partial<GlobalStore["toaster"]>;
        onError?: () => Partial<GlobalStore["toaster"]>;
      },
    ) => {
      if (options.onPending) {
        set((state) => ({
          toaster: {
            ...state.toaster,
            variant: "info" as AlertColor,
            open: true,
            duration: undefined,
            ...options.onPending?.(),
          },
        }));
      }

      const p = isFunction(promise) ? promise() : promise;
      let alreadySetToaster = false;

      //call the resolvers only when needed
      p.then((result: unknown) => {
        if (options.onSuccess) {
          alreadySetToaster = true;
          set((state) => ({
            toaster: {
              ...state.toaster,
              variant: "success" as AlertColor,
              open: true,
              duration: defaultGlobalStore.toaster.duration,
              ...options.onSuccess?.(),
            },
          }));
        }
        return result;
      })
        .catch((err: unknown) => {
          if (options.onError) {
            alreadySetToaster = true;
            set((state) => ({
              toaster: {
                ...state.toaster,
                variant: "error" as AlertColor,
                open: true,
                duration: defaultGlobalStore.toaster.duration,
                ...options.onError?.(),
              },
            }));
          }

          return err;
        })
        .finally(() => {
          if (!alreadySetToaster) {
            set((state) => ({
              toaster: {
                ...state.toaster,
                duration: defaultGlobalStore.toaster.duration,
                open: false,
              },
            }));
          }
        });

      return p;
    },
  },
}));

export const useToasterStore = () => useGlobalStore((state) => state.toaster);
export const useToasterStoreActions = () =>
  useGlobalStore((state) => state.actions);
